import FroalaEditor from 'froala-editor';

/**
 * FroalaDropdownUtils - Utility functions for creating custom Froala dropdown menus
 */
/**
 * HTML templates for different dropdown types
 */
export const HTML_TEMPLATES = {
  // Text formatting dropdown template
  TEXT_FORMATTING: `
      <div class="fr-dropdown-list" role="presentation">
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="underline" role="option" title="Underline">
          {{ICON:underline}}
          <span class="fr-sr-only">Underline</span>
        </a>
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="strikeThrough" role="option" title="Strikethrough">
          {{ICON:strikeThrough}}
          <span class="fr-sr-only">Strikethrough</span>
        </a>
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="subscript" role="option" title="Subscript">
          {{ICON:subscript}}
          <span class="fr-sr-only">Subscript</span>
        </a>
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="superscript" role="option" title="Superscript">
          {{ICON:superscript}}
          <span class="fr-sr-only">Superscript</span>
        </a>
      </div>
    `,

  // Indent formatting dropdown template
  INDENT_FORMATTING: `
      <div class="fr-dropdown-list" role="presentation">
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="outdent" role="option" title="Decrease Indent">
          {{ICON:outdent}}
          <span class="fr-sr-only">Decrease Indent</span>
        </a>
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="indent" role="option" title="Increase Indent">
          {{ICON:indent}}
          <span class="fr-sr-only">Increase Indent</span>
        </a>
      </div>
    `,

  // Color formatting dropdown template
  COLOR_FORMATTING: `
      <div class="fr-dropdown-list" role="presentation">
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="textColor" role="option" title="Text Color">
          {{ICON:textColor}}
          <span class="fr-sr-only">Text Color</span>
        </a>
        <a class="fr-command fr-btn" data-cmd="ttExecuteDefaultCmd" data-param1="backgroundColor" role="option" title="Background Color">
          {{ICON:backgroundColor}}
          <span class="fr-sr-only">Background Color</span>
        </a>
      </div>
    `,
};

export const COMMAND_ID_PREFIXES = {
  TEXT_FORMATTING: 'text_format_',
  INDENT_FORMATTING: 'indent_format_',
  COLOR_FORMATTING: 'color_format_',
};

/**
 * Generate HTML for a dropdown with icons
 * @param template The HTML template to use
 * @param editor The Froala Editor instance
 * @returns HTML string with icons
 */
export const generateDropdownHtml = (template: string, editor: FroalaEditor): string => {
  // Replace icon placeholders with actual icons
  return template.replace(/\{\{ICON:([^}]+)\}\}/g, (match, iconName) => {
    return editor.icon.create(iconName);
  });
};

/**
 * Generate the HTML method for a dropdown command
 * @param templateKey Key of the template to use from HTML_TEMPLATES
 * @returns A function to be used as the html method in RegisterCommand
 */
export const getHtmlMethod = (templateKey: keyof typeof HTML_TEMPLATES) => {
  return function (this: FroalaEditor) {
    return generateDropdownHtml(HTML_TEMPLATES[templateKey], this);
  };
};

export const colorFormattingRefreshOnShow = (): void => {
  // Does not need an action!
};

/**
 * Default refresh method for text formatting dropdown.
 * Updates active states for bold, italic, underline, and strikethrough.
 * Checks the editor format state.
 */
export const textFormattingRefreshOnShow = (_btn: JQuery<HTMLElement>, dropdown: JQuery<HTMLElement>, editorContext: FroalaEditor) => {
  const editor = editorContext;
  const dropdownEl = dropdown[0];
  const commandButtons = dropdownEl.querySelectorAll('a.fr-command');

  commandButtons.forEach((button) => {
    const formatCmd = button.getAttribute('data-param1');
    if (formatCmd) {
      let isActive = false;

      // Check active state based on command
      if (formatCmd === 'underline') {
        isActive = editor.format.is('u');
      } else if (formatCmd === 'strikeThrough') {
        isActive = editor.format.is('s');
      } else if (formatCmd === 'subscript') {
        isActive = editor.format.is('sub');
      } else if (formatCmd === 'superscript') {
        isActive = editor.format.is('sup');
      }

      // Toggle active class
      if (isActive) {
        button.classList.add('fr-active');
      } else {
        button.classList.remove('fr-active');
      }
    }
  });
};

/**
 * Refresh method for list formatting dropdown
 * Updates active states for lists and indentation
 */
export const indentFormattingRefreshOnShow = (_btn: JQuery<HTMLElement>, dropdown: JQuery<HTMLElement>, editorContext: FroalaEditor) => {
  const editor = editorContext;
  const dropdownEl = dropdown[0];
  const commandButtons = dropdownEl.querySelectorAll('a.fr-command');
  const blocks = editor.selection.blocks();
  let canOutdent = false;

  if (blocks?.length) {
    const computedStyle = window.getComputedStyle(blocks[0]);
    const marginLeft = parseInt(computedStyle.marginLeft, 10) || 0;
    const paddingLeft = parseInt(computedStyle.paddingLeft, 10) || 0;
    const hasIndentClass =
      blocks[0].classList.contains('fr-indent-1') ||
      blocks[0].classList.contains('fr-indent-2') ||
      blocks[0].classList.contains('fr-indent-3');
    canOutdent = marginLeft > 0 || paddingLeft > 0 || hasIndentClass;
  }

  commandButtons.forEach((button) => {
    const cmdName = button.getAttribute('data-param1');

    if (cmdName === 'outdent') {
      if (!canOutdent) {
        button.classList.add('fr-disabled');
      } else {
        button.classList.remove('fr-disabled');
      }
    }
  });
};

/**
 * Register a dropdown command with the specified settings
 * @param commandName Unique name for the command
 * @param title Title for the dropdown button
 * @param templateKey Key of the template to use in the constant templates
 * @param iconName Name of the icon for the dropdown button
 * @param iconSvgKey Name of the icon for the dropdown button
 * @param refreshMethod Refresh method to use
 */
export const registerDropdown = (
  commandName: string,
  title = '',
  templateKey: keyof typeof HTML_TEMPLATES,
  iconName = 'cog', // The gear icon
  iconSvgKey = 'cogs', // The gear icon svg key
  refreshMethod?: (btn: JQuery<HTMLElement>, dropdown: JQuery<HTMLElement>, editorContext: FroalaEditor) => void,
): void => {
  // Define the default icon for the dropdown
  FroalaEditor.DefineIcon(commandName, { NAME: iconName, SVG_KEY: iconSvgKey });

  const refreshOnShow = refreshMethod || textFormattingRefreshOnShow;

  FroalaEditor.RegisterCommand(commandName, {
    title: title,
    type: 'dropdown',
    html: getHtmlMethod(templateKey),
    callback: function () {
      return false;
    },
    refreshOnShow: function (_btn, dropdown) {
      refreshOnShow(_btn, dropdown, this);
    },
  });

  // Register the icon's default Froala command if it doesn't exist yet
  //@ts-ignore - The COMMANDS property is editable and extendable!
  if (!FroalaEditor.COMMANDS.ttExecuteDefaultCmd) {
    FroalaEditor.RegisterCommand('ttExecuteDefaultCmd', {
      title: 'Format',
      callback: function (cmd, val) {
        if (val === 'textColor' || val === 'backgroundColor') {
          this.commands.exec(val);
          return false;
        }

        this.commands.exec(val);
        this.popups.hideAll();
      },
    });
  }
};
